/**
 * Chunked Audio Player
 * Manages seamless playback of chunked audio from ElevenLabs processing
 * Handles sequential chunk loading and playback coordination
 */

class ChunkedAudioPlayer {
    constructor() {
        this.currentRequestId = null;
        this.currentChunkIndex = -1;
        this.totalChunks = 0;
        this.isPlaying = false;
        this.isPaused = false;
        this.chunks = new Map(); // chunk_index -> chunk_data
        this.audioElements = new Map(); // chunk_index -> HTMLAudioElement
        this.playbackQueue = [];
        this.chunkTimingOffsets = new Map(); // chunk_index -> timing_offset_ms
        
        // Playback coordination
        this.currentAudio = null;
        this.nextAudio = null;
        this.playbackStartTime = null;
        this.globalPlaybackTime = 0;
        
        // Volume control
        this.volumeController = null;
        
        // Status tracking
        this.onChunkReady = null;
        this.onPlaybackStart = null;
        this.onPlaybackEnd = null;
        this.onError = null;
        
        console.log('ChunkedAudioPlayer initialized');
    }
    
    /**
     * Set the volume controller reference
     * @param {VolumeController} controller - The volume controller to use for audio volume
     */
    setVolumeController(controller) {
        this.volumeController = controller;
        console.log('Volume controller connected to ChunkedAudioPlayer');
    }
    
    /**
     * Start processing a request with chunked audio
     * @param {string} requestId - The request ID for chunked processing
     * @param {Object} initialData - Initial chunk data from chat response
     */
    async startChunkedPlayback(requestId, initialData) {
        try {
            console.log(`Starting chunked playback for request: ${requestId}`);
            
            this.currentRequestId = requestId;
            this.totalChunks = initialData.total_chunks || 0;
            this.currentChunkIndex = -1;
            this.chunks.clear();
            this.audioElements.clear();
            this.chunkTimingOffsets.clear();
            this.playbackQueue = [];
            
            // Start monitoring chunk availability
            this.monitorChunkProgress();
            
            // Try to get the first chunk immediately
            setTimeout(() => this.checkForNextChunk(), 500);
            
        } catch (error) {
            console.error('Error starting chunked playback:', error);
            if (this.onError) this.onError(error);
        }
    }
    
    /**
     * Monitor the progress of chunk processing
     */
    async monitorChunkProgress() {
        if (!this.currentRequestId) return;
        
        try {
            const response = await fetch(`/api/chunked-audio/status/${this.currentRequestId}`);
            const status = await response.json();
            
            console.log('Chunk processing status:', status);
            
            if (status.status === 'completed') {
                console.log('All chunks completed');
                return;
            }
            
            if (status.status === 'error') {
                console.error('Chunk processing error:', status);
                if (this.onError) this.onError(new Error(status.message || 'Chunk processing failed'));
                return;
            }
            
            // Continue monitoring if still processing
            if (status.status === 'processing') {
                setTimeout(() => this.monitorChunkProgress(), 1000);
            }
            
        } catch (error) {
            console.error('Error monitoring chunk progress:', error);
            setTimeout(() => this.monitorChunkProgress(), 2000); // Retry with longer delay
        }
    }
    
    /**
     * Check for the next available chunk and prepare it for playback
     */
    async checkForNextChunk() {
        if (!this.currentRequestId) return;
        
        try {
            const response = await fetch(`/api/chunked-audio/next-chunk/${this.currentRequestId}?last_played_chunk=${this.currentChunkIndex}`);
            const result = await response.json();
            
            if (result.status === 'available') {
                console.log('Next chunk available:', result.chunk_data.chunk_index);
                await this.prepareChunk(result.chunk_data);
                
                // If this is the first chunk and we're not playing yet, start playback
                if (this.currentChunkIndex === -1 && !this.isPlaying) {
                    this.startPlayback();
                }
                
                // Continue checking for more chunks
                setTimeout(() => this.checkForNextChunk(), 500);
            } else {
                // Chunk not ready yet, check again soon
                setTimeout(() => this.checkForNextChunk(), 1000);
            }
            
        } catch (error) {
            console.error('Error checking for next chunk:', error);
            setTimeout(() => this.checkForNextChunk(), 2000);
        }
    }
    
    /**
     * Prepare a chunk for playback
     * @param {Object} chunkData - The chunk data from the backend
     */
    async prepareChunk(chunkData) {
        const chunkIndex = chunkData.chunk_index;
        
        if (this.chunks.has(chunkIndex)) {
            return; // Already prepared
        }
        
        try {
            // Store chunk data
            this.chunks.set(chunkIndex, chunkData);
            this.chunkTimingOffsets.set(chunkIndex, chunkData.timing_offset_ms || 0);
            
            // Create audio element from base64 data
            const audioBlob = this.base64ToBlob(chunkData.audio_base64, 'audio/mpeg');
            const audioUrl = URL.createObjectURL(audioBlob);
            
            const audio = new Audio(audioUrl);
            audio.preload = 'auto';
            
            // Apply volume control if available
            if (this.volumeController) {
                this.volumeController.applyVolumeToAudio(audio);
            }
            
            // Set up event listeners
            audio.addEventListener('canplaythrough', () => {
                console.log(`Chunk ${chunkIndex} ready for playback`);
                if (this.onChunkReady) this.onChunkReady(chunkIndex, chunkData);
            });
            
            audio.addEventListener('ended', () => {
                this.onChunkEnded(chunkIndex);
            });
            
            audio.addEventListener('error', (e) => {
                console.error(`Error loading chunk ${chunkIndex}:`, e);
            });
            
            this.audioElements.set(chunkIndex, audio);
            
            console.log(`Prepared chunk ${chunkIndex} (${chunkData.chunk_text.substring(0, 50)}...)`);
            
        } catch (error) {
            console.error(`Error preparing chunk ${chunkIndex}:`, error);
        }
    }
    
    /**
     * Start playback of the first available chunk
     */
    async startPlayback() {
        if (this.isPlaying || !this.audioElements.has(0)) {
            return;
        }
        
        console.log('Starting chunked audio playback');
        
        this.isPlaying = true;
        this.isPaused = false;
        this.currentChunkIndex = 0;
        this.playbackStartTime = Date.now();
        this.globalPlaybackTime = 0;
        
        if (this.onPlaybackStart) {
            this.onPlaybackStart();
        }
        
        await this.playChunk(0);
    }
    
    /**
     * Play a specific chunk
     * @param {number} chunkIndex - Index of the chunk to play
     */
    async playChunk(chunkIndex) {
        const audio = this.audioElements.get(chunkIndex);
        
        if (!audio) {
            console.error(`Audio for chunk ${chunkIndex} not available`);
            return;
        }
        
        try {
            this.currentAudio = audio;
            console.log(`Playing chunk ${chunkIndex}`);
            
            await audio.play();
            
        } catch (error) {
            console.error(`Error playing chunk ${chunkIndex}:`, error);
            if (this.onError) this.onError(error);
        }
    }
    
    /**
     * Handle when a chunk finishes playing
     * @param {number} chunkIndex - Index of the chunk that ended
     */
    onChunkEnded(chunkIndex) {
        console.log(`Chunk ${chunkIndex} ended`);
        
        const nextChunkIndex = chunkIndex + 1;
        
        // Update global playback time
        const chunkData = this.chunks.get(chunkIndex);
        if (chunkData && chunkData.total_duration_ms) {
            this.globalPlaybackTime += chunkData.total_duration_ms;
        }
        
        // Check if there's a next chunk to play
        if (nextChunkIndex < this.totalChunks && this.audioElements.has(nextChunkIndex)) {
            this.currentChunkIndex = nextChunkIndex;
            this.playChunk(nextChunkIndex);
        } else if (nextChunkIndex >= this.totalChunks) {
            // All chunks completed
            this.onPlaybackComplete();
        } else {
            // Next chunk not ready yet, wait for it
            console.log(`Waiting for chunk ${nextChunkIndex} to be ready`);
            this.waitForChunk(nextChunkIndex);
        }
    }
    
    /**
     * Wait for a specific chunk to become available
     * @param {number} chunkIndex - Index of the chunk to wait for
     */
    async waitForChunk(chunkIndex) {
        const maxWaitTime = 10000; // 10 seconds max wait
        const checkInterval = 100; // Check every 100ms
        let waitTime = 0;
        
        const checkChunk = () => {
            if (this.audioElements.has(chunkIndex)) {
                this.currentChunkIndex = chunkIndex;
                this.playChunk(chunkIndex);
                return;
            }
            
            waitTime += checkInterval;
            if (waitTime >= maxWaitTime) {
                console.error(`Timeout waiting for chunk ${chunkIndex}`);
                this.onPlaybackComplete();
                return;
            }
            
            setTimeout(checkChunk, checkInterval);
        };
        
        checkChunk();
    }
    
    /**
     * Handle playback completion
     */
    onPlaybackComplete() {
        console.log('Chunked audio playback completed');
        
        this.isPlaying = false;
        this.isPaused = false;
        this.currentAudio = null;
        
        if (this.onPlaybackEnd) {
            this.onPlaybackEnd();
        }
        
        // Clean up resources
        this.cleanup();
    }
    
    /**
     * Pause playback
     */
    pause() {
        if (this.currentAudio && this.isPlaying) {
            this.currentAudio.pause();
            this.isPaused = true;
            console.log('Chunked audio playback paused');
        }
    }
    
    /**
     * Resume playback
     */
    resume() {
        if (this.currentAudio && this.isPaused) {
            this.currentAudio.play();
            this.isPaused = false;
            console.log('Chunked audio playback resumed');
        }
    }
    
    /**
     * Stop playback
     */
    stop() {
        if (this.currentAudio) {
            // Force immediate stop by setting currentTime to end
            this.currentAudio.pause();
            this.currentAudio.currentTime = this.currentAudio.duration || 0;
            
            // Clear the source and load empty data to force stop
            this.currentAudio.src = '';
            this.currentAudio.load(); // Force reload to clear buffer
        }
        
        // Stop all other audio elements that might be playing
        this.audioElements.forEach((audio) => {
            if (audio && !audio.paused) {
                audio.pause();
                audio.currentTime = audio.duration || 0;
                audio.src = '';
                audio.load();
            }
        });
        
        this.isPlaying = false;
        this.isPaused = false;
        this.currentAudio = null;
        
        console.log('Chunked audio playback stopped');
        this.cleanup();
    }
    
    /**
     * Get current playback status
     */
    getStatus() {
        return {
            isPlaying: this.isPlaying,
            isPaused: this.isPaused,
            currentChunk: this.currentChunkIndex,
            totalChunks: this.totalChunks,
            globalPlaybackTime: this.globalPlaybackTime,
            requestId: this.currentRequestId
        };
    }
    
    /**
     * Clean up resources
     */
    cleanup() {
        // Revoke object URLs to free memory
        this.audioElements.forEach((audio) => {
            if (audio.src) {
                URL.revokeObjectURL(audio.src);
            }
        });
        
        // Clear data structures
        this.chunks.clear();
        this.audioElements.clear();
        this.chunkTimingOffsets.clear();
        
        // Clean up on server
        if (this.currentRequestId) {
            fetch(`/api/chunked-audio/cleanup/${this.currentRequestId}`, {
                method: 'POST'
            }).catch(error => {
                console.error('Error cleaning up server resources:', error);
            });
        }
        
        this.currentRequestId = null;
        this.currentChunkIndex = -1;
        this.totalChunks = 0;
    }
    
    /**
     * Convert base64 string to Blob
     * @param {string} base64 - Base64 encoded string
     * @param {string} mimeType - MIME type of the data
     * @returns {Blob} - Blob object
     */
    base64ToBlob(base64, mimeType) {
        const byteCharacters = atob(base64);
        const byteArrays = [];
        
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        
        return new Blob(byteArrays, { type: mimeType });
    }
}

// Export for use in other modules
window.ChunkedAudioPlayer = ChunkedAudioPlayer;